import React, { useContext } from 'react'
import type { LocalLink } from '../lib/predecessors'
import type { useScheduleMutations } from '../hooks/useScheduleMutations'
import type { ForeignPredecessor } from './GanttRow'
import type { RowOps } from './row-ops'

/** The `run` mutation queue's exact type, shared verbatim by every `GanttOps` consumer. */
export type RunFn = ReturnType<typeof useScheduleMutations>['run']

/**
 * Task 17: the single bundle `GanttView` reports upwards (via its `onOps` callback, Task 16) for
 * consumers that render outside its own JSX tree — the toolbar's `GanttEditingGroup` and the
 * activity panel. `detail/index.tsx` holds the latest value in state and wraps both consumers in
 * `GanttOpsContext.Provider`; `GanttView` itself never reads this context (it already owns every
 * field directly), it only ever writes it.
 */
export interface GanttOps {
  rowOps: RowOps
  run: RunFn
  predecessorsByActivity: Map<string, { local: LocalLink[]; foreign: ForeignPredecessor[] }>
  successorsByActivity: Map<string, { local: string[]; foreign: ForeignPredecessor[] }>
  /** 1-based display row number by activity id (the MS Project "ID" column), straight off `tree.rowNumberById` —
   * spec B §9.7 wants predecessor/successor rows labelled `rowNumber name` for local peers, same as the grid. */
  rowNumberById: Map<string, number>
  /** Inverse of `rowNumberById`, straight off `tree.idByRowNumber`. */
  idByRowNumber: Map<number, string>
  /** Focuses (and scrolls to) a local activity — same entry point the `?activity=` deep link and the activity log dialog use. */
  focusActivity: (id: string, opts?: { openPanel?: boolean }) => void
}

export const GanttOpsContext = React.createContext<GanttOps | null>(null)

export const useGanttOps = (): GanttOps => {
  const ctx = useContext(GanttOpsContext)
  if (!ctx) throw new Error('useGanttOps outside GanttOpsContext')
  return ctx
}
